import { inject, Injectable, signal } from '@angular/core';
import {
  Toast,
  ToastId,
  ToastOptions,
  ToastPromiseData,
  ToastPromiseResult,
  ToastType,
} from './types';

/** Time given to the exit animation before the toast is removed from the DOM. */
export const TIME_BEFORE_UNMOUNT = 200;

@Injectable({ providedIn: 'root' })
export class ToastService {
  readonly #toasts = signal<readonly Toast[]>([]);
  readonly #unmountTimers = new Map<ToastId, ReturnType<typeof setTimeout>>();
  #counter = 0;

  /** Toasts from newest to oldest, including the ones playing their exit animation. */
  readonly toasts = this.#toasts.asReadonly();

  show(title: string, options?: ToastOptions): ToastId {
    return this.#upsert('default', title, options);
  }

  success(title: string, options?: ToastOptions): ToastId {
    return this.#upsert('success', title, options);
  }

  info(title: string, options?: ToastOptions): ToastId {
    return this.#upsert('info', title, options);
  }

  warning(title: string, options?: ToastOptions): ToastId {
    return this.#upsert('warning', title, options);
  }

  error(title: string, options?: ToastOptions): ToastId {
    return this.#upsert('error', title, options);
  }

  /** Shows a toast that never closes itself until it is updated or dismissed. */
  loading(title: string, options?: ToastOptions): ToastId {
    return this.#upsert('loading', title, options);
  }

  /**
   * Shows a loading toast that turns into a success or error toast when the promise settles.
   * Observables can be passed with `firstValueFrom(observable)`.
   */
  promise<T>(
    promise: Promise<T> | (() => Promise<T>),
    data: ToastPromiseData<T>,
  ): ToastPromiseResult<T> {
    const { loading, success, error, finally: onFinally, ...options } = data;
    const id = this.loading(loading, options);
    let result: Promise<T>;
    try {
      result = Promise.resolve(typeof promise === 'function' ? promise() : promise);
    } catch (reason) {
      result = Promise.reject(reason);
    }

    const settle = (type: 'success' | 'error', title: string | undefined) => {
      if (title === undefined) {
        this.dismiss(id);
      } else {
        this.#upsert(type, title, { ...options, id });
      }
    };

    // A throwing message callback must not leave the loading toast forever.
    const onError = (reason: unknown) => {
      let title: string | undefined;
      try {
        title = typeof error === 'function' ? error(reason) : error;
      } catch {
        title = undefined;
      }
      settle('error', title);
    };
    const onSuccess = (value: T) => {
      let title: string | undefined;
      try {
        title = typeof success === 'function' ? success(value) : success;
      } catch (reason) {
        onError(reason);
        return;
      }
      settle('success', title);
    };

    result.then(onSuccess, onError).finally(() => onFinally?.());

    return { id, unwrap: () => result };
  }

  /** Dismisses a toast, or every toast when no id is given. */
  dismiss(id?: ToastId): void {
    const targets = this.#toasts().filter((t) => !t.removed && (id === undefined || t.id === id));
    for (const toast of targets) {
      this.#remove(toast);
      toast.onDismiss?.(toast);
    }
  }

  /** @internal Closes a toast without callback, after a click on its action or cancel button. */
  close(id: ToastId): void {
    const toast = this.#toasts().find((t) => t.id === id);
    if (toast && !toast.removed) {
      this.#remove(toast);
    }
  }

  /** @internal Called by the toast when its timer runs out. */
  autoClose(id: ToastId): void {
    const toast = this.#toasts().find((t) => t.id === id);
    if (toast && !toast.removed) {
      this.#remove(toast);
      toast.onAutoClose?.(toast);
    }
  }

  #upsert(type: ToastType, title: string, options: ToastOptions = {}): ToastId {
    const id = options.id ?? ++this.#counter;
    const existing = this.#toasts().find((t) => t.id === id);

    if (existing) {
      this.#cancelUnmount(id);
      const updated: Toast = {
        ...existing,
        ...options,
        id,
        type,
        title,
        version: existing.version + 1,
        removed: false,
      };
      this.#toasts.update((toasts) => toasts.map((t) => (t.id === id ? updated : t)));
    } else {
      const toast: Toast = { ...options, id, type, title, version: 0, removed: false };
      this.#toasts.update((toasts) => [toast, ...toasts]);
    }
    return id;
  }

  #remove(toast: Toast): void {
    this.#toasts.update((toasts) =>
      toasts.map((t) => (t.id === toast.id ? { ...t, removed: true } : t)),
    );
    this.#cancelUnmount(toast.id);
    this.#unmountTimers.set(
      toast.id,
      setTimeout(() => {
        this.#unmountTimers.delete(toast.id);
        this.#toasts.update((toasts) => toasts.filter((t) => t.id !== toast.id));
      }, TIME_BEFORE_UNMOUNT),
    );
  }

  #cancelUnmount(id: ToastId): void {
    clearTimeout(this.#unmountTimers.get(id));
    this.#unmountTimers.delete(id);
  }
}

export function injectToast(): ToastService {
  return inject(ToastService);
}
